'use client';

import { c as _c } from "react/compiler-runtime";
import { type ChangeEvent, type InputHTMLAttributes } from 'react';
import { FieldGroup, type FieldGroupType } from '@/app/components/forms/field-group';
import { type CustomFormStyleConfig, getCustomFormStyle, inputBoxStyled, inputIconStyled, inputTextStyled, selectDropdownIconStyled, SelectNativeOptionGroupStyles, SelectNativeOptionStyles, selectPlaceholderTextStyled } from '@/app/components/forms/styles';
import { Icon, type IconType } from '@/app/components/icons';
import { IconArrowDropDown } from '@/app/components/icons/ui-actions/icon-arrow-drop-down';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
export interface SelectOption {
  label?: string;
  value: string;
  disabled?: boolean;
}
export interface SelectOptionGroup {
  group: string;
  options: SelectOption[];
}
interface SelectProps extends Omit<FieldGroupType, 'length' | 'maxLength' | 'showMaxLength' | 'inputType'>, InputHTMLAttributes<HTMLSelectElement> {
  /**
   * The name of the input field.
   */
  name: string;
  /**
   * The options to be rendered as select options
   */
  options: SelectOption[] | SelectOptionGroup[];
  /**
   * (Optional) The default label to be displayed.
   */
  placeholder?: string;
  /**
   * (Optional) The value of the selected option.
   */
  value?: string;
  /**
   * (Optional) The icon to be displayed before the selec.
   */
  iconBefore?: IconType;
  /**
   * (Optional) The function to be called when the value changes.
   */
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  /**
   * (Optional) Whether the component is auto-completed.
   */
  autoComplete?: string;
  /**
   * (Optional) The class name of the component.
   */
  className?: string;

  /**
   * (Optional) The class name of the select element.
   */
  selectClassName?: string;

  /**
   * (Optional) The class name of the group element.
   */
  groupClassName?: string;

  /**
   * (Optional) Custom styles for the component.
   */
  customStyle?: CustomFormStyleConfig;
  hidePlaceholder?: boolean;
}
const renderChildren = (options: SelectOption[] | SelectOptionGroup[]) => {
  const isSelectOptionGroup = (option: SelectOption | SelectOptionGroup): option is SelectOptionGroup => 'options' in option;
  const renderOptions = (options: SelectOption[]) => options.map((option, index) => <option key={index} className={SelectNativeOptionStyles} value={option.value} disabled={option.disabled}>
        {option.label}
      </option>);
  const isGrouped = options.length > 0 && isSelectOptionGroup(options[0]);
  if (isGrouped) {
    return (options as SelectOptionGroup[]).map(group => <optgroup key={group.group} className={SelectNativeOptionGroupStyles} label={group.group}>
        {renderOptions(group.options)}
      </optgroup>);
  }
  return renderOptions(options as SelectOption[]);
};
export const Select = t0 => {
  const $ = _c(103);
  let className;
  let customStyle;
  let errorMessage;
  let groupClassName;
  let hidePlaceholder;
  let iconBefore;
  let label;
  let message;
  let name;
  let onChange;
  let props;
  let ref;
  let secondaryLabel;
  let selectClassName;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let value;
  if ($[0] !== t0) {
    ({
      label,
      secondaryLabel,
      errorMessage,
      message,
      theme: t1,
      disabled: t2,
      required: t3,
      error: t4,
      name,
      options: t5,
      placeholder: t6,
      value,
      autoComplete: t7,
      onChange,
      iconBefore,
      customStyle,
      className,
      selectClassName,
      groupClassName,
      ref,
      hidePlaceholder,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = customStyle;
    $[3] = errorMessage;
    $[4] = groupClassName;
    $[5] = hidePlaceholder;
    $[6] = iconBefore;
    $[7] = label;
    $[8] = message;
    $[9] = name;
    $[10] = onChange;
    $[11] = props;
    $[12] = ref;
    $[13] = secondaryLabel;
    $[14] = selectClassName;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = value;
  } else {
    className = $[1];
    customStyle = $[2];
    errorMessage = $[3];
    groupClassName = $[4];
    hidePlaceholder = $[5];
    iconBefore = $[6];
    label = $[7];
    message = $[8];
    name = $[9];
    onChange = $[10];
    props = $[11];
    ref = $[12];
    secondaryLabel = $[13];
    selectClassName = $[14];
    t1 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    value = $[22];
  }
  const theme = t1 === undefined ? "light" : t1;
  const disabled = t2 === undefined ? false : t2;
  const required = t3 === undefined ? false : t3;
  const error = t4 === undefined ? false : t4;
  let t8;
  if ($[23] !== t5) {
    t8 = t5 === undefined ? [] : t5;
    $[23] = t5;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const options = t8;
  const placeholder = t6 === undefined ? "" : t6;
  const autoComplete = t7 === undefined ? "off" : t7;
  const [, t9] = useDisclosure(false);
  const {
    open: onOpenFormSelect,
    close: onCloseFormSelect
  } = t9;
  let t10;
  if ($[25] !== onChange || $[26] !== onCloseFormSelect) {
    t10 = event => {
      onCloseFormSelect();
      safeCall(onChange, event);
    };
    $[25] = onChange;
    $[26] = onCloseFormSelect;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleChange = t10;
  let t11;
  if ($[28] !== onCloseFormSelect) {
    t11 = () => {
      onCloseFormSelect();
    };
    $[28] = onCloseFormSelect;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const handleBlur = t11;
  let t12;
  if ($[30] !== className || $[31] !== customStyle?.select?.wrapper || $[32] !== disabled || $[33] !== error || $[34] !== theme) {
    t12 = cn(inputBoxStyled({
      theme,
      error,
      disabled
    }), "group flex h-12 rounded-sm", getCustomFormStyle(customStyle?.select?.wrapper, {
      error,
      disabled
    }), className);
    $[30] = className;
    $[31] = customStyle?.select?.wrapper;
    $[32] = disabled;
    $[33] = error;
    $[34] = theme;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const wrapperClass = t12;
  const hasValue = value !== undefined && value !== "";
  const isPlaceholderVisible = !hasValue;
  let t13;
  if ($[36] !== customStyle?.select?.placeholder || $[37] !== customStyle?.select?.text || $[38] !== disabled || $[39] !== error || $[40] !== iconBefore || $[41] !== isPlaceholderVisible || $[42] !== selectClassName || $[43] !== theme) {
    const systemPlaceholderClass = isPlaceholderVisible ? selectPlaceholderTextStyled({
      theme,
      error,
      disabled
    }) : "";
    const customPlaceholderClass = getCustomFormStyle(isPlaceholderVisible ? customStyle?.select?.placeholder : customStyle?.select?.text, {
      error,
      disabled
    });
    t13 = cn(inputTextStyled({
      theme,
      error,
      disabled
    }), "cursor-pointer self-stretch overflow-hidden pr-12 pl-4 text-ellipsis", {
      "pl-12": !!iconBefore
    }, systemPlaceholderClass, customPlaceholderClass, selectClassName);
    $[36] = customStyle?.select?.placeholder;
    $[37] = customStyle?.select?.text;
    $[38] = disabled;
    $[39] = error;
    $[40] = iconBefore;
    $[41] = isPlaceholderVisible;
    $[42] = selectClassName;
    $[43] = theme;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const selectClass = t13;
  let t14;
  if ($[45] !== customStyle?.select?.icon || $[46] !== disabled || $[47] !== error || $[48] !== theme) {
    t14 = cn(inputIconStyled({
      theme,
      error,
      disabled
    }), "absolute top-0 bottom-0 left-4 my-auto", getCustomFormStyle(customStyle?.select?.icon, {
      error,
      disabled
    }));
    $[45] = customStyle?.select?.icon;
    $[46] = disabled;
    $[47] = error;
    $[48] = theme;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  const iconClass = t14;
  let t15;
  if ($[50] !== customStyle?.select?.dropdownIcon || $[51] !== disabled || $[52] !== error || $[53] !== hasValue || $[54] !== theme) {
    t15 = cn(selectDropdownIconStyled({
      theme,
      error,
      disabled
    }), "center-y absolute right-4", {
      "text-gray-800": hasValue && !disabled && theme === "light",
      "text-white": hasValue && !disabled && theme === "dark",
      "group-focus-within:text-gray-200 group-hover:text-gray-200": !hasValue && !disabled && !error && theme === "light",
      "group-focus-within:text-gray-400 group-hover:text-gray-400": !hasValue && !disabled && !error && theme === "dark"
    }, getCustomFormStyle(customStyle?.select?.dropdownIcon, {
      error,
      disabled
    }));
    $[50] = customStyle?.select?.dropdownIcon;
    $[51] = disabled;
    $[52] = error;
    $[53] = hasValue;
    $[54] = theme;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  const dropdownIconClass = t15;
  const t16 = customStyle?.fieldGroup;
  const t17 = label ? ref : undefined;
  const t18 = label ? undefined : ref;
  let t19;
  if ($[56] !== iconBefore || $[57] !== iconClass) {
    t19 = !!iconBefore && <Icon icon={iconBefore} className={iconClass} />;
    $[56] = iconBefore;
    $[57] = iconClass;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconArrowDropDown />;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== dropdownIconClass) {
    t21 = <Icon icon={t20} className={dropdownIconClass} />;
    $[60] = dropdownIconClass;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== onOpenFormSelect) {
    t22 = () => onOpenFormSelect();
    $[62] = onOpenFormSelect;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  const t23 = `select-${name}`;
  let t24;
  if ($[64] !== t23) {
    t24 = createDataAttributes({
      test: t23
    });
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== hidePlaceholder || $[67] !== placeholder) {
    t25 = !hidePlaceholder && <option className={SelectNativeOptionStyles} value="">{placeholder || "\u0E01\u0E23\u0E38\u0E13\u0E32\u0E40\u0E25\u0E37\u0E2D\u0E01"}</option>;
    $[66] = hidePlaceholder;
    $[67] = placeholder;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== options) {
    t26 = renderChildren(options);
    $[69] = options;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== autoComplete || $[72] !== disabled || $[73] !== handleBlur || $[74] !== handleChange || $[75] !== name || $[76] !== selectClass || $[77] !== t22 || $[78] !== t24 || $[79] !== t25 || $[80] !== t26 || $[81] !== value) {
    t27 = <select id={name} name={name} value={value} disabled={disabled} autoComplete={autoComplete} onMouseDown={t22} onBlur={handleBlur} onChange={handleChange} className={selectClass} {...t24}>{t25}{t26}</select>;
    $[71] = autoComplete;
    $[72] = disabled;
    $[73] = handleBlur;
    $[74] = handleChange;
    $[75] = name;
    $[76] = selectClass;
    $[77] = t22;
    $[78] = t24;
    $[79] = t25;
    $[80] = t26;
    $[81] = value;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== t18 || $[84] !== t19 || $[85] !== t21 || $[86] !== t27 || $[87] !== wrapperClass) {
    t28 = <div className={wrapperClass} ref={t18}>{t19}{t21}{t27}</div>;
    $[83] = t18;
    $[84] = t19;
    $[85] = t21;
    $[86] = t27;
    $[87] = wrapperClass;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== error || $[90] !== errorMessage || $[91] !== groupClassName || $[92] !== label || $[93] !== message || $[94] !== name || $[95] !== props || $[96] !== required || $[97] !== secondaryLabel || $[98] !== t16 || $[99] !== t17 || $[100] !== t28 || $[101] !== theme) {
    t29 = <FieldGroup {...props} htmlFor={name} label={label} secondaryLabel={secondaryLabel} required={required} error={error} errorMessage={errorMessage} message={message} theme={theme} customStyle={t16} className={groupClassName} ref={t17}>{t28}</FieldGroup>;
    $[89] = error;
    $[90] = errorMessage;
    $[91] = groupClassName;
    $[92] = label;
    $[93] = message;
    $[94] = name;
    $[95] = props;
    $[96] = required;
    $[97] = secondaryLabel;
    $[98] = t16;
    $[99] = t17;
    $[100] = t28;
    $[101] = theme;
    $[102] = t29;
  } else {
    t29 = $[102];
  }
  return t29;
};